(function () {
  const GIFT_PROPERTY = '_ru_gift_milestone';
  const GIFT_PROPERTY_2 = '_ru_gift_milestone_2';
  const GIFT_VALUE = '1';
  const SOURCE = 'ru-cart-gift-milestone';

  class RuCartGiftMilestone {
    constructor() {
      this.syncing = false;
      this.pendingSync = false;
      this.syncTimer = null;
      this.syncPromise = null;
      this.lastSyncResult = { action: null, refreshed: false };
      this.cartUpdateUnsubscriber = null;
    }

    init() {
      const config = window.ruGiftMilestoneConfig;
      const config2 = window.ruGiftMilestoneConfig2;

      const hasGift1 = config?.enabled && config?.variantId;
      const hasGift2 = config2?.enabled && config2?.variantId;

      if (!hasGift1 && !hasGift2) return;

      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === SOURCE) return;
          this.syncNow(event?.cartData).catch((error) => console.error(error));
        });
      }

      this.syncNow().catch((error) => console.error(error));
    }

    destroy() {
      if (this.syncTimer) window.clearTimeout(this.syncTimer);
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    }

    scheduleSync() {
      return this.syncNow();
    }

    isFullCart(cart) {
      return Boolean(cart && typeof cart.item_count === 'number' && Array.isArray(cart.items));
    }

    syncNow(cartData) {
      if (this.syncTimer) {
        window.clearTimeout(this.syncTimer);
        this.syncTimer = null;
      }

      if (!this.syncPromise) {
        this.syncPromise = this.sync(cartData).finally(() => {
          this.syncPromise = null;
        });
      } else if (this.syncing) {
        this.pendingSync = true;
      }

      return this.syncPromise;
    }

    isGiftPropertyKey(key) {
      return typeof key === 'string' && key.indexOf('_ru_gift_milestone') === 0;
    }

    isGiftLine(item) {
      const properties = item?.properties;
      if (!properties) return false;

      return Object.keys(properties).some(
        (key) => this.isGiftPropertyKey(key) && String(properties[key]) === GIFT_VALUE
      );
    }

    hasGiftProperty(item, property) {
      return item?.properties?.[property] === GIFT_VALUE;
    }

    getVariantId(item) {
      return Number(item?.variant_id || item?.id) || 0;
    }

    getActiveGiftSlots() {
      return [
        { number: 1, property: GIFT_PROPERTY, config: window.ruGiftMilestoneConfig },
        { number: 2, property: GIFT_PROPERTY_2, config: window.ruGiftMilestoneConfig2 },
      ].filter((slot) => slot.config?.enabled && slot.config?.variantId);
    }

    getEligibleTotal(cart) {
      if (!cart?.items?.length) return 0;

      return cart.items.reduce((sum, item) => {
        if (this.isGiftLine(item)) return sum;
        return sum + (item.final_line_price || 0);
      }, 0);
    }

    getGiftLines(cart, property) {
      return cart.items
        .map((item, index) => ({ item, line: index + 1 }))
        .filter(({ item }) =>
          property ? this.hasGiftProperty(item, property) : this.isGiftLine(item)
        );
    }

    getLinesForVariant(cart, variantId) {
      const targetId = Number(variantId);
      return cart.items
        .map((item, index) => ({ item, line: index + 1 }))
        .filter(({ item }) => this.getVariantId(item) === targetId);
    }

    cartHasVariant(cart, variantId) {
      const targetId = Number(variantId);
      return cart.items.some((item) => this.getVariantId(item) === targetId);
    }

    async sync(prefetchedCart) {
      const config = window.ruGiftMilestoneConfig;
      const config2 = window.ruGiftMilestoneConfig2;

      const hasGift1 = config?.enabled && config?.variantId;
      const hasGift2 = config2?.enabled && config2?.variantId;

      if (!hasGift1 && !hasGift2) {
        this.lastSyncResult = { action: null, refreshed: false };
        return this.lastSyncResult;
      }

      /*if (!config?.enabled || !config.variantId) {
        this.lastSyncResult = { action: null, refreshed: false };
        return this.lastSyncResult;
      }*/

      if (this.syncing) {
        this.pendingSync = true;
        return this.syncPromise || this.lastSyncResult;
      }

      this.syncing = true;

      try {
        do {
          this.pendingSync = false;
          this.lastSyncResult = await this.syncOnce(prefetchedCart);
          prefetchedCart = null;
        } while (this.pendingSync);

        return this.lastSyncResult;
      } finally {
        this.syncing = false;
      }
    }

    async syncOnce(prefetchedCart) {
      const sectionConfig = this.getSectionConfig();
      let cart = this.isFullCart(prefetchedCart)
        ? prefetchedCart
        : await fetch('/cart.js').then((response) => response.json());
      let response = null;
      let actions = [];

      // Safety: at most 1 unit per gift variant (with or without gift properties).
      const dedupe = await this.enforceGiftUniqueness(cart, sectionConfig);
      if (dedupe.response) {
        response = dedupe.response;
        actions = actions.concat(dedupe.actions);
        cart = this.isFullCart(response)
          ? response
          : await fetch('/cart.js').then((res) => res.json());
      }

      const thresholdResult = await this.syncGiftThresholds(cart, sectionConfig);
      if (thresholdResult.response) {
        response = thresholdResult.response;
        actions = actions.concat(thresholdResult.actions);
      }

      if (response) {
        await this.applyCartResponse(response, sectionConfig);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: SOURCE,
            cartData: response,
            refreshed: true,
          });
        }
        return { action: actions, refreshed: true };
      }

      if (sectionConfig.type === 'page') {
        await this.refreshMilestonesOnly();
        return { action: null, refreshed: true };
      }

      return { action: null, refreshed: false };
    }

    async syncGiftThresholds(cart, sectionConfig) {
      const config = window.ruGiftMilestoneConfig;
      const config2 = window.ruGiftMilestoneConfig2;
      let response = null;
      const actions = [];

      const runPass = async () => {
        const eligibleTotal = this.getEligibleTotal(cart);
        const threshold = Number(config?.thresholdCents) || 0;
        const threshold2 = Number(config2?.thresholdCents) || 0;
        const giftLines1 = this.getGiftLines(cart, GIFT_PROPERTY);
        const giftLines2 = this.getGiftLines(cart, GIFT_PROPERTY_2);
        const shouldHaveGift = Boolean(config?.enabled && threshold > 0 && eligibleTotal >= threshold);
        const shouldHaveGift2 = Boolean(config2?.enabled && threshold2 > 0 && eligibleTotal >= threshold2);

        // Removals first (highest line first) so indexes stay stable for remaining lines.
        const removeLines = [];

        if (!shouldHaveGift && giftLines1.length > 0) {
          giftLines1.forEach((entry) => removeLines.push(entry));
        } else if (shouldHaveGift && giftLines1.length > 1) {
          giftLines1.slice(1).forEach((entry) => removeLines.push(entry));
        }

        if (!shouldHaveGift2 && giftLines2.length > 0) {
          giftLines2.forEach((entry) => removeLines.push(entry));
        } else if (shouldHaveGift2 && giftLines2.length > 1) {
          giftLines2.slice(1).forEach((entry) => removeLines.push(entry));
        }

        if (removeLines.length) {
          const unique = [];
          const seen = new Set();
          removeLines
            .sort((a, b) => b.line - a.line)
            .forEach((entry) => {
              if (seen.has(entry.line)) return;
              seen.add(entry.line);
              unique.push(entry);
            });

          actions.push({ action: 'remove', giftLines: unique });
          response = await this.removeGifts(unique, sectionConfig);
          cart = this.isFullCart(response)
            ? response
            : await fetch('/cart.js').then((res) => res.json());
          return true;
        }

        // Normalize qty to 1.
        const normalizeTargets = [];
        if (shouldHaveGift && giftLines1.length === 1 && giftLines1[0].item.quantity !== 1) {
          normalizeTargets.push(giftLines1[0].line);
        }
        if (shouldHaveGift2 && giftLines2.length === 1 && giftLines2[0].item.quantity !== 1) {
          normalizeTargets.push(giftLines2[0].line);
        }

        if (normalizeTargets.length) {
          for (const line of [...new Set(normalizeTargets)].sort((a, b) => b - a)) {
            actions.push({ action: 'normalize', line });
            response = await this.setGiftQuantity(line, 1, sectionConfig);
            cart = this.isFullCart(response)
              ? response
              : await fetch('/cart.js').then((res) => res.json());
          }
          return true;
        }

        // Adds last, only if variant is not already present.
        let added = false;

        if (shouldHaveGift && giftLines1.length === 0 && !this.cartHasVariant(cart, config.variantId)) {
          actions.push({ action: 'add', gift: 1 });
          response = await this.addGift(sectionConfig, 1);
          cart = this.isFullCart(response)
            ? response
            : await fetch('/cart.js').then((res) => res.json());
          added = true;
        }

        if (shouldHaveGift2 && this.getGiftLines(cart, GIFT_PROPERTY_2).length === 0 && !this.cartHasVariant(cart, config2.variantId)) {
          actions.push({ action: 'add', gift: 2 });
          response = await this.addGift(sectionConfig, 2);
          cart = this.isFullCart(response)
            ? response
            : await fetch('/cart.js').then((res) => res.json());
          added = true;
        }

        return added;
      };

      // Up to a few passes in case removals unlock another fix.
      for (let i = 0; i < 4; i += 1) {
        const changed = await runPass();
        if (!changed) break;
      }

      return { response, actions };
    }

    /**
     * Ensures each configured gift variant exists at most once in the cart
     * (qty 1), whether lines have gift properties or not.
     * Different gift products may coexist.
     */
    async enforceGiftUniqueness(cart, sectionConfig) {
      const slots = this.getActiveGiftSlots();
      if (!slots.length || !cart?.items?.length) {
        return { response: null, actions: [] };
      }

      const eligibleTotal = this.getEligibleTotal(cart);
      const byVariant = new Map();

      slots.forEach((slot) => {
        const variantId = Number(slot.config.variantId);
        if (!byVariant.has(variantId)) {
          byVariant.set(variantId, { slots: [], lines: [] });
        }
        byVariant.get(variantId).slots.push(slot);
      });

      cart.items.forEach((item, index) => {
        const variantId = this.getVariantId(item);
        if (!byVariant.has(variantId)) return;
        byVariant.get(variantId).lines.push({ item, line: index + 1 });
      });

      const linesToRemove = new Set();
      const actions = [];

      byVariant.forEach(({ slots: variantSlots, lines }) => {
        if (!lines.length) return;

        const shouldKeep = variantSlots.some((slot) => {
          const threshold = Number(slot.config.thresholdCents) || 0;
          return threshold > 0 && eligibleTotal >= threshold;
        });

        if (!shouldKeep) {
          lines.forEach(({ line }) => linesToRemove.add(line));
          return;
        }

        const preferredProperties = variantSlots
          .filter((slot) => {
            const threshold = Number(slot.config.thresholdCents) || 0;
            return threshold > 0 && eligibleTotal >= threshold;
          })
          .map((slot) => slot.property);

        const giftLines = lines.filter(({ item }) => this.isGiftLine(item));

        if (giftLines.length > 0) {
          const keeper =
            giftLines.find(({ item }) =>
              preferredProperties.some((property) => this.hasGiftProperty(item, property))
            ) || giftLines[0];

          lines.forEach(({ line }) => {
            if (line !== keeper.line) linesToRemove.add(line);
          });
          return;
        }

        // Manual/non-gift copies of the gift product: remove all; threshold sync will re-add.
        lines.forEach(({ line }) => linesToRemove.add(line));
      });

      // Orphan gift lines (property present, variant no longer configured).
      cart.items.forEach((item, index) => {
        if (!this.isGiftLine(item)) return;
        const variantId = this.getVariantId(item);
        if (!byVariant.has(variantId)) {
          linesToRemove.add(index + 1);
        }
      });

      let response = null;

      const removeLines = [...linesToRemove].sort((a, b) => b - a);
      if (removeLines.length) {
        actions.push({ action: 'dedupe_remove', lines: removeLines });
        response = await this.removeGifts(
          removeLines.map((line) => ({ line })),
          sectionConfig
        );
      }

      // Normalize any remaining gift lines to qty 1.
      const freshCart = response
        ? this.isFullCart(response)
          ? response
          : await fetch('/cart.js').then((res) => res.json())
        : cart;

      for (const slot of slots) {
        const giftLines = this.getGiftLines(freshCart, slot.property);
        for (const { item, line } of giftLines) {
          if (item.quantity === 1) continue;
          actions.push({ action: 'normalize', line });
          response = await this.setGiftQuantity(line, 1, sectionConfig);
        }
      }

      return { response, actions };
    }

    addGift(sectionConfig, giftNumber = 1) {
      const config = giftNumber === 2
        ? window.ruGiftMilestoneConfig2
        : window.ruGiftMilestoneConfig;

      const property = giftNumber === 2
        ? GIFT_PROPERTY_2
        : GIFT_PROPERTY;

      const payload = {
        items: [
          {
            id: Number(config.variantId),
            quantity: 1,
            properties: {
              [property]: GIFT_VALUE,
            },
          },
        ],
      };

      if (sectionConfig?.sections) {
        payload.sections = sectionConfig.sections;
        payload.sections_url = sectionConfig.sections_url;
      }

      return fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        ...fetchConfig(),
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((state) => {
          if (state.status) throw new Error(state.description || state.message);
          return state;
        });
    }

    removeGifts(giftLines, sectionConfig) {
      if (!giftLines.length) return Promise.resolve(null);

      const sortedLines = [...giftLines].sort((a, b) => b.line - a.line);

      return sortedLines.reduce((promise, { line }) => {
        return promise.then((lastState) =>
          this.setGiftQuantity(line, 0, sectionConfig).then((state) => state || lastState)
        );
      }, Promise.resolve(null));
    }

    setGiftQuantity(line, quantity, sectionConfig) {
      return fetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', {
        ...fetchConfig(),
        body: JSON.stringify({
          line,
          quantity,
          sections: sectionConfig?.sections,
          sections_url: sectionConfig?.sections_url,
        }),
      })
        .then((response) => response.json())
        .then((state) => {
          if (state.status) throw new Error(state.description || state.message);
          return state;
        });
    }

    getSectionConfig() {
      const cartPageItems = document.querySelector('cart-items.cart-page');
      if (cartPageItems && typeof cartPageItems.getSectionsToRender === 'function') {
        return {
          type: 'page',
          sections: cartPageItems.getSectionsToRender().map((section) => section.section).join(','),
          sections_url: window.location.pathname,
          cartItems: cartPageItems,
        };
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        return {
          type: 'drawer',
          sections: drawer.getSectionsToRender().map((section) => section.id).join(','),
          sections_url: window.location.pathname,
          drawer,
        };
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
        return {
          type: 'page',
          sections: cartItems.getSectionsToRender().map((section) => section.section).join(','),
          sections_url: window.location.pathname,
          cartItems,
        };
      }

      return {
        type: 'icon',
        sections: 'cart-icon-bubble',
        sections_url: window.location.pathname,
      };
    }

    async applyCartResponse(response, sectionConfig) {
      if (response?.sections && sectionConfig) {
        this.renderCart(response, sectionConfig);

        if (sectionConfig.type === 'page') {
          await this.refreshMilestonesOnly();
          return;
        }

        if (sectionConfig.type === 'drawer') {
          this.refreshDrawerRecommendations();
          return;
        }
      }

      await this.refreshCartFromServer(sectionConfig);
    }

    refreshDrawerRecommendations() {
      const host = document.querySelector('cart-drawer-recommendations');
      if (!host) return;

      fetch('/cart.js')
        .then((response) => response.json())
        .then((cartData) => {
          if (!cartData.items?.length) {
            host.hide?.();
            return;
          }

          const firstPaidItem = cartData.items.find((item) => !this.isGiftLine(item)) || cartData.items[0];
          host.dataset.productId = String(firstPaidItem.product_id);
          host.tryLoad?.();
        })
        .catch(() => host.tryLoad?.());
    }

    async refreshMilestonesOnly() {
      const cartFooter = document.getElementById('main-cart-footer');
      if (!cartFooter) return;

      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const footerHtml = await fetch(`${cartUrl}?section_id=main-cart-footer`).then((response) => response.text());
      const footerDoc = new DOMParser().parseFromString(footerHtml, 'text/html');
      const sourceMilestones = footerDoc.querySelector('.ru-cart-milestones');
      const targetMilestones = cartFooter.querySelector('.ru-cart-milestones');

      if (sourceMilestones && targetMilestones) {
        targetMilestones.replaceWith(sourceMilestones);
      } else if (sourceMilestones && !targetMilestones) {
        const cartBlocks = cartFooter.querySelector('.cart__blocks');
        if (cartBlocks) {
          cartBlocks.parentNode.insertBefore(sourceMilestones, cartBlocks);
        }
      } else if (!sourceMilestones && targetMilestones) {
        targetMilestones.remove();
      }
    }

    async refreshCartFromServer(sectionConfig) {
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

      if (sectionConfig?.type === 'drawer') {
        const [html, iconHtml, cart] = await Promise.all([
          fetch(`${cartUrl}?section_id=cart-drawer`).then((response) => response.text()),
          fetch(`${cartUrl}?section_id=cart-icon-bubble`).then((response) => response.text()),
          fetch('/cart.js').then((response) => response.json()),
        ]);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const drawer = document.querySelector('cart-drawer');
        const itemCount = cart?.item_count ?? 0;
        const isEmpty = itemCount === 0;

        if (drawer) {
          drawer.classList.toggle('is-empty', isEmpty);
        }

        if (isEmpty) {
          const sourceInner = doc.querySelector('.drawer__inner');
          const targetInner = drawer?.querySelector('.drawer__inner');
          if (sourceInner && targetInner) {
            targetInner.innerHTML = sourceInner.innerHTML;
          }
        } else {
          ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
            const targetElement = document.querySelector(selector);
            const sourceElement = doc.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          });

          const emptyTarget = drawer?.querySelector('.drawer__inner-empty');
          if (emptyTarget) emptyTarget.remove();

          const recTarget = document.querySelector('cart-drawer-recommendations');
          const recSource = doc.querySelector('cart-drawer-recommendations');
          if (recTarget && recSource) {
            recTarget.replaceWith(recSource);
          } else if (recTarget && !recSource) {
            recTarget.remove();
          } else if (!recTarget && recSource && drawer) {
            const footer = drawer.querySelector('.drawer__footer');
            if (footer) footer.before(recSource);
          }
        }

        const iconDoc = new DOMParser().parseFromString(iconHtml, 'text/html');
        const iconSource = iconDoc.querySelector('.shopify-section');
        const iconTarget = document.getElementById('cart-icon-bubble');
        if (iconSource && iconTarget) {
          iconTarget.innerHTML = iconSource.innerHTML;
        }

        return;
      }

      if (sectionConfig?.type === 'page' && sectionConfig.cartItems) {
        const [itemsHtml, footerHtml, iconHtml] = await Promise.all([
          fetch(`${cartUrl}?section_id=main-cart-items`).then((response) => response.text()),
          fetch(`${cartUrl}?section_id=main-cart-footer`).then((response) => response.text()),
          fetch(`${cartUrl}?section_id=cart-icon-bubble`).then((response) => response.text()),
        ]);

        const itemsDoc = new DOMParser().parseFromString(itemsHtml, 'text/html');
        const sourceCartItems = itemsDoc.querySelector('cart-items');
        if (sourceCartItems) {
          sectionConfig.cartItems.innerHTML = sourceCartItems.innerHTML;
        }

        const footerDoc = new DOMParser().parseFromString(footerHtml, 'text/html');
        const cartFooter = document.getElementById('main-cart-footer');

        const sourceMilestones = footerDoc.querySelector('.ru-cart-milestones');
        const targetMilestones = cartFooter?.querySelector('.ru-cart-milestones');
        if (sourceMilestones && targetMilestones) {
          targetMilestones.replaceWith(sourceMilestones);
        }

        const sourceFooter = footerDoc.querySelector('.js-contents');
        const targetFooter = cartFooter?.querySelector('.js-contents');
        if (sourceFooter && targetFooter) {
          targetFooter.innerHTML = sourceFooter.innerHTML;
        }

        const sourceCheckout = footerDoc.querySelector('.cart__ctas');
        const targetCheckout = cartFooter?.querySelector('.cart__ctas');
        if (sourceCheckout && targetCheckout) {
          targetCheckout.innerHTML = sourceCheckout.innerHTML;
        }

        const iconDoc = new DOMParser().parseFromString(iconHtml, 'text/html');
        const iconSource = iconDoc.querySelector('.shopify-section');
        const iconTarget = document.getElementById('cart-icon-bubble');
        if (iconSource && iconTarget) {
          iconTarget.innerHTML = iconSource.innerHTML;
        }
      }
    }

    renderCart(parsedState, sectionConfig) {
      if (!parsedState?.sections || !sectionConfig) return;

      if (sectionConfig.type === 'drawer' && sectionConfig.drawer) {
        const drawer = sectionConfig.drawer;
        const wasActive = drawer.classList.contains('active');

        // change.js includes item_count; add.js does not (gift add → leave empty state).
        if (typeof parsedState.item_count === 'number') {
          drawer.classList.toggle('is-empty', parsedState.item_count === 0);
        } else {
          drawer.classList.remove('is-empty');
        }

        sectionConfig.drawer.getSectionsToRender().forEach((section) => {
          const sectionHtml = parsedState.sections[section.id];
          if (!sectionHtml) return;

          const elementToReplace = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!elementToReplace) return;

          elementToReplace.innerHTML = drawer.getSectionInnerHTML(sectionHtml, section.selector);
        });

        if (wasActive) {
          drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
        }

        return;
      }

      if (sectionConfig.type === 'page' && sectionConfig.cartItems) {
        const cartItems = sectionConfig.cartItems;
        const itemCount = parsedState.item_count ?? parsedState.items?.length ?? 0;

        cartItems.classList.toggle('is-empty', itemCount === 0);

        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', itemCount === 0);

        cartItems.getSectionsToRender().forEach((section) => {
          const sectionHtml = parsedState.sections[section.section];
          if (!sectionHtml) return;

          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (!elementToReplace) return;

          elementToReplace.innerHTML = cartItems.getSectionInnerHTML(sectionHtml, section.selector);
        });

        return;
      }

      const bubbleHtml = parsedState.sections['cart-icon-bubble'];
      if (!bubbleHtml) return;

      const bubbleSection = document.getElementById('cart-icon-bubble');
      if (!bubbleSection) return;

      const parsed = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const source = parsed.querySelector('.shopify-section');
      if (source) bubbleSection.innerHTML = source.innerHTML;
    }
  }

  const ruCartGiftMilestone = new RuCartGiftMilestone();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ruCartGiftMilestone.init());
  } else {
    ruCartGiftMilestone.init();
  }

  window.ruCartGiftMilestone = ruCartGiftMilestone;
})();
